import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  ColumnFiltersState,
} from '@tanstack/react-table'
import {
  Accordion,
  ActionPopUp,
  SvgAccordionArrow,
  Modal,
  CustomButton,
  ControlledDropDown,
  FormInput,
} from '@ui-kit'
import {
  addToaster,
  changeTaskStatusToWithdraw,
  CreateChildInstanceType,
  DataType,
  DropdownType,
  fetchCaseIdUsingWorkFlowAndDefinitionId,
  fetchEntityCategory,
  getAllWorkflowListUsingServiceId,
  getGenericEntityListingAPI,
  getLayoutByIdApi,
  hideLoader,
  procedureForCreateOrder,
  showLoader,
  WORKFLOW_UI_IDENTIFIER,
} from '@app-store'
import { isEmpty, noop } from 'lodash'
import { v4 as uuid } from 'uuid'
import { FieldValues, useForm } from 'react-hook-form'
import {
  CreateOrderForm,
  EnrollmentApiResponse,
  NestedTableComponentProps,
  ShipmentColumn,
  ShippingDetailsComponentProps,
  ItemType,
  ShippingDetailsForm,
} from '../../types/workflow.types'
import {
  APPLICATION_PREFERENCES_STRINGS,
  MULTI_UPLOAD_UTILS,
  STRINGS,
} from '../../utils/strings'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import moment from 'moment'
import FormRenderer from '../FormRenderer'
import { EntityLayout, LayoutData } from '../../types/layout.types'
import { editEntityData, getEntityData } from '../../apis/entityData.api'
import { capitalizeWord } from '../../utils/form.utils'
import { RECORD_STATE } from '../../utils/constants'
import { isSuperAdmin } from '../../utils/permission'

const ShipmentSubTable = ({
  shipments,
  shipmentColumns,
}: ShippingDetailsComponentProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const table = useReactTable({
    data: shipments,
    columns: shipmentColumns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const getAccordionHeadingShipment = () => (
    <div className="flex justify-between items-center">
      <span className="font-medium text-lg">{STRINGS.SHIPMENT_DETAILS}</span>
    </div>
  )

  return (
    <div className="mt-2 w-full">
      <Accordion heading={getAccordionHeadingShipment()} alwaysOpen>
        <div className="max-w-full overflow-x-auto">
          <div className="border border-gray-300 rounded-md shadow-sm w-full">
            <table className="table-auto border-collapse text-sm w-max">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-t">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-left bg-secondary-50 px-3 py-2 font-normal whitespace-nowrap border"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t font-extralight hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 border whitespace-nowrap text-gray-700"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Accordion>
    </div>
  )
}

export const NestedTableComponent = ({
  task,
  reFetch,
  recordId,
  tabIndex,
}: NestedTableComponentProps) => {
  const dispatch = useAppDispatch()
  const [expanded, setExpanded] = useState({})
  const [enrollmentTableColumns, setEnrollmentTableColumns] = useState<
    ShipmentColumn[]
  >([])
  const [shipmentTableColumns, setShipmentTableColumns] = useState<
    ShipmentColumn[]
  >([])
  const workflowInstanceSelector = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const recordIdValue = recordId?.toString() ?? ''
  const fillType = useAppSelector(
    (state) => state.categoryDropdown?.categoryMap?.[STRINGS.FILL_TYPE]
  )
  const fulfillmentType = useAppSelector(
    (state) => state.categoryDropdown?.categoryMap?.[STRINGS.FULFILLMENT_TYPE]
  )
  const authUser = useAppSelector((state) => state.authUser)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const programServices = useAppSelector(
    (state) => state?.clientPermissions?.programServices
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [primaryListSelectedRow, setPrimaryListSelectedRow] = useState<
    string | null
  >(null)
  const [refetch, setRefetch] = useState(false)
  const [enrollmentData, setEnrollmentData] =
    useState<EnrollmentApiResponse | null>()
  const [layoutData, setLayoutData] = useState<FieldValues | null>()
  const [layoutDataForWithdrawal, setLayoutDataForWithdrawal] =
    useState<FieldValues | null>()
  const [dataForWithdrawal, setDataForWithdrawal] =
    useState<FieldValues | null>()
  const [layoutEntityData, setLayoutEntityData] = useState<FieldValues | null>(
    null
  )
  const [enrollmentDataForRecord, setEnrollmentDataForRecord] =
    useState<EnrollmentApiResponse | null>()
  const [fillTypeDropdown, setFillTypeDropdown] = useState<DropdownType[]>([])
  const [fulFillmentTypeDropdown, setFulFillmentTypeDropdown] = useState<
    DropdownType[]
  >([])
  const caseDataRef = useRef<FieldValues>()
  const [distributorTypeDropdown, setDistributorTypeDropdown] =
    useState<FieldValues>()
  const canExpandRow = useCallback(
    (row: FieldValues) =>
      row?.original?.c_case_shipment_details_case?.length > 0,
    []
  )
  const data = useMemo(
    () =>
      Array.isArray(enrollmentData?.c_cases_parent_case)
        ? enrollmentData?.c_cases_parent_case
        : [],
    [enrollmentData?.c_cases_parent_case]
  )
  const columns = useMemo(
    () => enrollmentTableColumns,
    [enrollmentTableColumns]
  )
  const table = useReactTable({
    data,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: canExpandRow,
  })
  const rowModel = table?.getRowModel()
  const [shipToEntityDropDownValues, setShipToEntityDropDownValues] = useState(
    []
  )
  const { register, setValue, getValues, control, reset } = useForm()
  const getAccordionHeading = () => {
    return (
      <div className="flex justify-between items-center">
        {task?.identifier?.sub_ui_identifier !==
          WORKFLOW_UI_IDENTIFIER.CUSTOM_BRIDGE_LAYOUT && (
          <span className="font-medium text-lg">{`${STRINGS.ENROLLMENT_ID} ${
            enrollmentDataForRecord?.c_pap_enrollment_details_case
              ?.enrollment_key
              ? ' : ' +
                enrollmentDataForRecord?.c_pap_enrollment_details_case
                  ?.enrollment_key
              : ''
          }`}</span>
        )}

        <div className="flex gap-4 pr-1 items-center">
          {task?.identifier?.sub_ui_identifier !==
            WORKFLOW_UI_IDENTIFIER.CUSTOM_BRIDGE_LAYOUT && (
            <span
              className={`${
                enrollmentDataForRecord?.c_pap_enrollment_details_case
                  ?.enrollment_status?.name === STRINGS.IN_ACTIVE
                  ? 'bg-red-400 text-red-700'
                  : enrollmentDataForRecord?.c_pap_enrollment_details_case
                      ?.enrollment_status?.name === STRINGS.PENDING
                  ? 'bg-taskStatus-300 text-black'
                  : 'bg-green-300 text-green-700'
              } px-3 py-1 rounded`}
            >
              <span className="text-black">{STRINGS.STATUS} : </span>
              {` ${
                enrollmentDataForRecord?.c_pap_enrollment_details_case
                  ?.enrollment_status?.name ?? ''
              }`}
            </span>
          )}
          <ActionPopUp
            actions={[
              ...(enrollmentDataForRecord?.c_pap_enrollment_details_case
                ?.enrollment_status?.name === STRINGS.ACTIVE
                ? [
                    {
                      label: STRINGS.CREATE_ORDER,
                      key: STRINGS.CREATE_ORDER,
                      tabIndex: tabIndex + 999,
                    },
                  ]
                : []),
              {
                label: STRINGS.WITHDRAW,
                key: STRINGS.WITHDRAW,
                tabIndex: tabIndex + 999,
              },
            ]}
            row={[]}
            rowIndex={999}
            onActionClick={onActionClick}
            setRowPosition={noop}
            isHovered={true}
            isDisabled={
              enrollmentDataForRecord?.c_pap_enrollment_details_case
                ?.enrollment_status?.name === STRINGS.WITHDRAW ||
              enrollmentDataForRecord?.c_pap_enrollment_details_case
                ?.enrollment_status?.name === STRINGS.WITHDRAWN_STATUS
            }
          />
        </div>
      </div>
    )
  }

  const fetchLayoutData = async () => {
    try {
      dispatch(showLoader())
      const response = await getEntityData(
        layoutData?.entity_name,
        Number(layoutData?.entity_id),
        Number(layoutData?.id),
        Number(recordIdValue)
      )

      const dataToShow = {
        ...response.data,
        case_order_details: {
          fulfillment_type: fulfillmentType?.find(
            (item: FieldValues) => item?.v_order === 1
          ),

          fill_type: fillType?.find((item: FieldValues) => item?.v_order === 1),
        },
      }
      setLayoutEntityData(dataToShow)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchLayout = async () => {
    dispatch(showLoader())
    try {
      const response = await getLayoutByIdApi({
        entityId: workflowDefinition?.global_variables?.entity_id,
        layoutId: task?.identifier?.layout_info?.order?.layout_id ?? '',
      })
      if (response?.data) {
        setLayoutData(response.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const parseEnrollment = (apiResult: FieldValues) => {
    if (!apiResult) return apiResult

    const parsedData = { ...apiResult }

    if (Array.isArray(parsedData.c_cases_parent_case)) {
      parsedData.c_cases_parent_case = parsedData.c_cases_parent_case.map(
        (caseItem) => {
          const updatedItem = { ...caseItem }

          if (updatedItem?.c_case_statuses?.name) {
            updatedItem.c_case_statuses = updatedItem.c_case_statuses.name
          }

          if (updatedItem?.users) {
            const { first_name = '', last_name = '' } = updatedItem.users
            updatedItem.users = `${first_name} ${last_name}`.trim()
          }

          if (updatedItem?.created_at) {
            updatedItem.created_at = moment(updatedItem.created_at).format(
              'MM/DD/YYYY'
            )
          }
          if (updatedItem?.modified_at) {
            updatedItem.modified_at = moment(updatedItem.modified_at).format(
              'MM/DD/YYYY'
            )
          }
          if (Array.isArray(updatedItem?.c_case_shipment_details_case)) {
            updatedItem.c_case_shipment_details_case =
              updatedItem.c_case_shipment_details_case.map(
                (shipment: FieldValues) => {
                  const flattened: FieldValues = {}

                  for (const [key, value] of Object.entries(shipment) as [
                    string,
                    FieldValues
                  ][]) {
                    if (key === 'shipment_date' && value) {
                      flattened[key] = moment(value).format('MM/DD/YYYY')
                      continue
                    }

                    if (
                      value &&
                      typeof value === 'object' &&
                      !Array.isArray(value)
                    ) {
                      const possibleValue =
                        value.id ??
                        value.label ??
                        value.title ??
                        value.display ??
                        value.code ??
                        Object.values(value).find(
                          (v) => typeof v === 'string'
                        ) ??
                        ''

                      flattened[key] = possibleValue
                      continue
                    }

                    flattened[key] = value ?? ''
                  }

                  return flattened
                }
              )
          }

          return updatedItem
        }
      )
    }

    return parsedData
  }

  const fetchDataForEnrollmentAPI = async (
    orderEntityColumns: ShipmentColumn[],
    shipmentEntityColumns: ShipmentColumn[]
  ) => {
    try {
      const newPayload = {
        entity_name: workflowDefinition?.global_variables?.entity_name,
        select: ['case_key', 'c_cases_parent_case', 'services'],
        criteria: [],
        related_entities: {
          c_cases_parent_case: {
            select: [
              'case_key',
              'services',
              'c_case_shipment_details_case',
              ...(orderEntityColumns?.map((val) => val?.accessorKey) ?? []),
            ],
            criteria: [],
            related_entities: {
              services: {
                select: ['id', 'service_name'],
                criteria: [],
                filters: [
                  {
                    criteria: [
                      {
                        values: [65],
                        operation: 'EQUALS',
                        field_name: 'id',
                      },
                    ],
                  },
                ],
              },
              c_case_shipment_details_case: {
                select: [
                  'product_name',
                  ...(shipmentEntityColumns?.map((val) => val?.accessorKey) ??
                    []),
                ],
                criteria: [],
              },
            },
          },
        },
        filters: [],
      }

      const caseIdObject1 = await getGenericEntityListingAPI(
        newPayload,
        recordId ?? ''
      )

      const parsedData = parseEnrollment(caseIdObject1)
      setEnrollmentData(parsedData as EnrollmentApiResponse)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const enrollmentColumns = useMemo<ColumnDef<EnrollmentApiResponse>[]>(() => {
    return [
      {
        id: 'expander',
        header: '',
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                row.toggleExpanded()
              }}
              className="cursor-pointer px-2 py-1 bg-transparent"
            >
              <SvgAccordionArrow
                className={`transform transition-transform duration-200 ${
                  row.getIsExpanded() ? 'rotate-180' : ''
                }`}
              />
            </button>
          ) : null,
      },
    ]
  }, [])

  const fetchLayoutDataForEnrollment = async () => {
    try {
      const layout = {
        layoutId: task?.identifier?.layout_info?.enrollment?.layout_id,
        entityId: task?.identifier?.layout_info?.enrollment?.entity_id,
      }
      const layoutData = await getLayoutByIdApi(layout)
      const columns =
        layoutData?.data?.layout_data?.pages?.[0]?.rows?.[0]?.columns?.[0]?.elements?.map(
          (item: ItemType) => {
            if (item?.keyName === 'case_key') {
              return {
                accessorKey: item?.keyName,
                header: item?.meta?.label,
                type: 'hyperlink',
                cell: ({ row }: FieldValues) => {
                  const value = row.getValue(item?.keyName)

                  return (
                    <span
                      className="text-blue-500 underline cursor-pointer"
                      onClick={() =>
                        window.open(
                          `/entity/${workflowDefinition?.global_variables?.entity_id}/workflow/review/` +
                            row?.original?.id,
                          '_blank'
                        )
                      }
                    >
                      {value}
                    </span>
                  )
                },
              }
            }

            return {
              accessorKey: item?.keyName,
              header: item?.meta?.label,
              type: 'text',
            }
          }
        )

      if (columns?.length) {
        setEnrollmentTableColumns([
          ...columns,
          ...enrollmentColumns,
        ] as ShipmentColumn[])
        return columns
      }
      return []
    } catch (error) {
      console.error(error)
    }
  }

  const fetchLayoutDatForShipment = async () => {
    try {
      const layout = {
        layoutId: task?.identifier?.layout_info?.shipment?.layout_id,
        entityId: task?.identifier?.layout_info?.shipment?.entity_id,
      }
      const layoutData = await getLayoutByIdApi(layout)
      const columns =
        layoutData?.data?.layout_data?.pages?.[0]?.rows?.[0]?.columns?.[0]?.elements?.map(
          (item: ItemType) => ({
            accessorKey: item?.keyName,
            header: item?.meta?.label,
          })
        )
      if (columns?.length) {
        setShipmentTableColumns(columns as ShipmentColumn[])
        return columns
      }
      return []
    } catch (error) {
      console.error(error)
    }
  }
  const fetchEnrollmentIdData = async () => {
    try {
      if (recordId) {
        const newPayload2 = {
          entity_name: workflowDefinition?.global_variables?.entity_name,
          select: ['c_pap_enrollment_details_case'],
          criteria: [],
          related_entities: {
            c_pap_enrollment_details_case: {
              select: ['enrollment_key', 'enrollment_status'],
              criteria: [],
              filter: [],
            },
          },
          filters: [],
        }
        const caseIdObject2 = await getGenericEntityListingAPI(
          newPayload2,
          recordId ?? ''
        )
        setEnrollmentDataForRecord(caseIdObject2)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchData = async () => {
    dispatch(showLoader())
    const [orderColumns, shipmentColumns] = await Promise.all([
      fetchLayoutDataForEnrollment(),
      fetchLayoutDatForShipment(),
    ])
    fetchEnrollmentIdData()
    fetchDataForEnrollmentAPI(
      orderColumns as ShipmentColumn[],
      shipmentColumns as ShipmentColumn[]
    )
  }

  const getPreferredShipTo = (e?: DropdownType<DataType, string>) => {
    const shipTo = getValues('ship_to_entity')?.toLowerCase()
    const patient = STRINGS.PATIENT
    const facility = STRINGS.FACILITY
    if (e?.value) return e.value
    if (shipTo?.includes(patient)) return capitalizeWord(patient)
    if (shipTo?.includes(facility)) return capitalizeWord(facility)
    return ''
  }
  const procedureForApiCall = async (e?: DropdownType<DataType, string>) => {
    try {
      dispatch(showLoader())

      await procedureForCreateOrder(recordIdValue, getPreferredShipTo(e) ?? '')

      const payload2 = {
        entity_name: workflowDefinition?.global_variables?.entity_name,
        criteria: [],
        select: ['c_case_shipment_address_case'],
        related_entities: {
          c_case_shipment_address_case: {
            select: [
              'id',
              'address_line_1',
              'address_line_2',
              'ship_to_entity',
              'city',
              'state',
              'zipcode',
            ],
            criteria: [],
          },
        },
      }
      const caseIdObject2 = await getGenericEntityListingAPI(
        payload2,
        recordIdValue
      )
      const formData = {
        address_line_1:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.address_line_1 ||
          '',
        address_line_2:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.address_line_2 ||
          '',
        city: caseIdObject2?.c_case_shipment_address_case?.[0]?.city || '',
        state: caseIdObject2?.c_case_shipment_address_case?.[0]?.state || '',
        zipcode:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.zipcode || '',
      }
      Object.entries(formData).forEach(([key, value]) => setValue(key, value))
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchLayoutForWithdrawal = async () => {
    try {
      dispatch(showLoader())
      const response = await getLayoutByIdApi({
        entityId: task?.identifier?.layout_info?.withdrawal?.entity_id,
        layoutId: task?.identifier?.layout_info?.withdrawal?.layout_id ?? '',
      })
      if (response?.data) {
        setLayoutDataForWithdrawal(response.data)
        let responseForEntity = await getEntityData(
          response?.data?.entity_name,
          Number(response?.data?.entity_id),
          Number(response?.data?.id),
          Number(task?.resource_id)
        )
        const payload = {
          select: ['name'],
          entity_name: STRINGS.PAP_ENROLLMENT_STATUS,
        }
        const listData = await getGenericEntityListingAPI(payload)
        const withdrawal_reason = listData?.find(
          (item: FieldValues) =>
            item?.name === STRINGS.WITHDRAW ||
            item?.name === STRINGS.WITHDRAWN_STATUS
        )
        responseForEntity = {
          ...responseForEntity?.data,
          ...(!responseForEntity?.data?.withdrawal_date && {
            withdrawal_date: moment().format('YYYY-MM-DD'),
          }),
          ...(responseForEntity?.data?.enrollment_status?.name !==
            STRINGS.WITHDRAWN && {
            enrollment_status: withdrawal_reason,
          }),
        }
        setDataForWithdrawal(responseForEntity)
      }
      setIsModalOpen(true)
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const onActionClick = (key: string) => {
    setPrimaryListSelectedRow(key)
    switch (key) {
      case STRINGS.WITHDRAW:
        fetchLayoutForWithdrawal()

        return
      case STRINGS.CREATE_ORDER:
        fetchLayout()
        fetchListForShipToEntity()
        fetchDropdownOptionsCreateOrder()
        return
      default:
        return null
    }
  }
  const fetchDropdownOptionsCreateOrder = async () => {
    try {
      const layoutFillType = {
        entity_id: task?.identifier?.layout_info?.order?.fill_type
          ? +task?.identifier?.layout_info?.order?.fill_type
          : 0,
        select: ['id', 'category', 'value', 'vorder'],
        filters: [
          {
            criteria: [
              {
                field_name: 'category',
                operation: 'EQUALS',
                values: ['fill type'],
              },
            ],
          },
        ],
      }
      const layoutFulfillmentType = {
        ...layoutFillType,
        filters: layoutFillType.filters.map((filter) => ({
          ...filter,
          criteria: filter.criteria.map((c) => ({
            ...c,
            values: ['fulfillment type'],
          })),
        })),
      }

      const data = await fetchEntityCategory(layoutFillType)
      const dropDownValues = data?.data?.map((val: FieldValues) => {
        return { ...val, label: val?.value }
      })
      const dataToSet = dropDownValues?.find(
        (item: FieldValues) => item?.vorder === 1
      )
      setValue('fill_type', {
        id: dataToSet?.id,
        _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
      })

      setFillTypeDropdown(dropDownValues)
      const data2 = await fetchEntityCategory(layoutFulfillmentType)
      const dropDownValues2 = data2?.data?.map((val: FieldValues) => {
        return { ...val, label: val?.value }
      })
      const dataToSet2 = dropDownValues2?.find(
        (item: FieldValues) => item?.vorder === 1
      )
      setValue('fulfillment_type', {
        id: dataToSet2?.id,
        _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
      })
      setFulFillmentTypeDropdown(dropDownValues2)

      const payload = {
        entity_name: 'd_entity_fields',
        select: [
          'id',
          'field_name',
          'd_entity_id',
          'control_values',
          'default_value',
        ],
        criteria: [],
        filters: [
          {
            criteria: [
              {
                values: [
                  task?.identifier?.layout_info?.order?.distributor ?? '',
                ],
                operation: 'EQUALS',
                field_name: 'd_entity_id',
              },
              {
                values: ['distributor_name'],
                operation: 'EQUALS',
                field_name: 'field_name',
              },
            ],
          },
        ],
      }
      const caseIdObject1 = await getGenericEntityListingAPI(payload)
      setValue('distributor', caseIdObject1?.[0]?.default_value)
      setDistributorTypeDropdown(caseIdObject1?.[0])
    } catch (error) {
      console.error(error)
    }
  }
  const fetchListForShipToEntity = async () => {
    try {
      const payload = {
        entity_name: 'd_entity_fields',
        select: ['id', 'field_name', 'd_entity_id', 'control_values'],
        criteria: [],
        filters: [
          {
            criteria: [
              {
                values: [
                  task?.identifier?.layout_info?.order?.ship_to_entity ?? '',
                ],
                operation: 'EQUALS',
                field_name: 'd_entity_id',
              },
              {
                values: ['ship_to_entity'],
                operation: 'EQUALS',
                field_name: 'field_name',
              },
            ],
          },
        ],
      }
      const caseIdObject1 = await getGenericEntityListingAPI(payload)
      const transformed = caseIdObject1?.[0]?.control_values?.map(
        (item: FieldValues) => ({
          value: item,
          label: item,
        })
      )
      setShipToEntityDropDownValues(transformed)
      const payload2 = {
        entity_name: workflowDefinition?.global_variables?.entity_name,
        criteria: [],
        select: [
          'id',
          'case_pap_details',
          'c_case_shipment_address_case',
          'products',
          'intake_channel_id',
          'requestor',
        ],
        related_entities: {
          case_pap_details: {
            select: ['id', 'shipping_preference'],
            criteria: [],
          },
          c_case_shipment_address_case: {
            criteria: [],
            select: [
              'id',
              'address_line_1',
              'address_line_2',
              'ship_to_entity',
              'city',
              'state',
              'zipcode',
            ],
          },
        },
      }
      const caseIdObject2 = await getGenericEntityListingAPI(
        payload2,
        recordIdValue
      )
      const pref =
        caseIdObject2?.case_pap_details?.shipping_preference?.toLowerCase()
      const formData = {
        ship_to_entity: pref?.includes(STRINGS.PATIENT)
          ? capitalizeWord(STRINGS.PATIENT)
          : pref?.includes(STRINGS.FACILITY) || pref?.includes(STRINGS.OFFICE)
          ? capitalizeWord(STRINGS.FACILITY)
          : caseIdObject2?.case_pap_details?.shipping_preference,
        address_line_1:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.address_line_1 ||
          '',
        address_line_2:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.address_line_2 ||
          '',
        city: caseIdObject2?.c_case_shipment_address_case?.[0]?.city || '',
        state: caseIdObject2?.c_case_shipment_address_case?.[0]?.state || '',
        zipcode:
          caseIdObject2?.c_case_shipment_address_case?.[0]?.zipcode || '',
      }
      caseDataRef.current = {
        ...(caseIdObject2?.products?.id && {
          products: {
            id: caseIdObject2.products.id,
            _state: RECORD_STATE.ADDED,
          },
        }),

        ...(caseIdObject2?.requestor?.id && {
          requestor: {
            id: caseIdObject2.requestor.id,
            _state: RECORD_STATE.ADDED,
          },
        }),

        ...(caseIdObject2?.intake_channel_id && {
          intake_channel_id: caseIdObject2.intake_channel_id,
        }),
      }

      Object.entries(formData).forEach(([key, value]) => setValue(key, value))
      procedureForApiCall()
      setIsModalOpen(true)
    } catch (error) {
      console.log(error)
      dispatch(hideLoader())
    } finally {
      dispatch(hideLoader())
    }
  }
  const withdrawalApiCall = async (formData: FieldValues) => {
    try {
      dispatch(showLoader())
      await editEntityData(
        layoutDataForWithdrawal?.entity_name,
        layoutDataForWithdrawal?.entity_id,
        layoutDataForWithdrawal?.id,
        formData,
        Number(task?.resource_id)
      )
      if (workflowInstanceSelector?.id) {
        await changeTaskStatusToWithdraw(workflowInstanceSelector?.id ?? '')
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
      reFetch?.()
    }
  }
  const SubmitButton = () => {
    return (
      <div
        className={`flex justify-between items-center gap-4 mt-4 ${
          primaryListSelectedRow === STRINGS.WITHDRAW ? 'px-2' : 'border p-2'
        }  rounded`}
      >
        {primaryListSelectedRow === STRINGS.WITHDRAW ? (
          <div className="flex flex-col items-start pb-2">
            <span className="text-medium font-medium">
              {layoutDataForWithdrawal?.entity?.singular_label ?? ''}
            </span>
            <span className="text-sm mt-0.5">{STRINGS.EDIT}</span>
          </div>
        ) : (
          <span className="text-medium font-medium">
            {STRINGS.HEADING_FOR_ORDER_CASE}
          </span>
        )}
        <div className="flex gap-3">
          <CustomButton
            onClick={() => {
              setIsModalOpen(false)
              setPrimaryListSelectedRow(null)
              reset()
            }}
          >
            {APPLICATION_PREFERENCES_STRINGS.CANCEL}
          </CustomButton>
          <CustomButton isFilled type="submit">
            {APPLICATION_PREFERENCES_STRINGS.SUBMIT}
          </CustomButton>
        </div>
      </div>
    )
  }
  const onUpdateClickWithdraw = async (formData: FieldValues) => {
    setIsModalOpen(false)

    await withdrawalApiCall(formData)
  }
  const handleFormSubmit = async (values: FieldValues) => {
    try {
      const data = getValues()
      if (isEmpty(data?.ship_to_entity)) {
        dispatch(
          addToaster({
            message: STRINGS.FILL_REQUIRED_FIELDS,
            type: 'info',
            id: uuid(),
          })
        )
        return
      }
      dispatch(showLoader())

      const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
      const response = await getAllWorkflowListUsingServiceId(
        workflowDefinition?.id ? +workflowDefinition?.id : 0,
        workflowDefinition?.program_id ? +workflowDefinition?.program_id : 0
      )
      const idForServiceOrderCase = response?.data?.filter(
        (item: FieldValues) =>
          item?.master_id === task?.identifier?.layout_info?.order?.master_id
      )
      const programId = workflowDefinition?.program_id
      const serviceId = idForServiceOrderCase?.[0]?.service_id

      const fetchCasePayload: CreateChildInstanceType = {
        process_request: {
          flow_meta_data: {
            process_definition_id: idForServiceOrderCase?.[0]?.id ?? '',
            communication_id: 0,
          },
          meta_data: {
            entity_name:
              workflowDefinition?.global_variables?.entity_name ?? '',
            embedded: ['case_order_details', 'c_provider'],
          },
          data: {
            ...caseDataRef.current,
            case_order_details: {
              fill_type: data?.fill_type,
              fulfillment_type: data?.fulfillment_type,
              distributor_name: data?.distributor,
            },
            c_provider: {
              ...values?.c_provider,
              _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
            },
            ...(isSuperAdmin() ||
            programServices[programId ?? '']?.includes(serviceId ?? 0)
              ? {
                  users: {
                    id: Number(authUser?.user?.id),
                    _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
                  },
                }
              : {
                  users: {
                    _state: 'REMOVED',
                  },
                }),
          },
        },
        procedure_param: {
          show_intake_form: false,
          case_id: +recordIdValue,
          source_id: idForServiceOrderCase?.[0]?.id ?? '',
        },
      }
      await fetchCaseIdUsingWorkFlowAndDefinitionId(
        fetchCasePayload,
        idForServiceOrderCase?.[0]?.id ?? '',
        workflowDefinition?.id ? +workflowDefinition?.id : 0,
        reviewer ?? ''
      )
      dispatch(
        addToaster({
          message: STRINGS.ORDER_CASE_MESSAGE,
          type: 'success',
          id: uuid(),
        })
      )
    } catch (error) {
      dispatch(
        addToaster({
          message: STRINGS.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uuid(),
        })
      )
      console.error(error)
    } finally {
      if (getValues()?.ship_to_entity) {
        dispatch(hideLoader())
        reset()
        setIsModalOpen(false)
        setPrimaryListSelectedRow(null)
        setRefetch((prev) => !prev)
      }
    }
  }
  useEffect(() => {
    if (fillType && fulfillmentType && layoutData) {
      fetchLayoutData()
    }
  }, [fillType, fulfillmentType, layoutData])
  useEffect(() => {
    if (task && workflowDefinition) {
      fetchData()
    }
  }, [task, refetch, workflowDefinition])
  return (
    <Accordion heading={getAccordionHeading()} alwaysOpen>
      <form>
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            showCloseButton={false}
            closeModal={() => {
              setIsModalOpen(false)
              setPrimaryListSelectedRow(null)
              reset()
            }}
            width="min-w-[40rem]"
            bodyCssClassName="min-h-[20rem]"
          >
            {primaryListSelectedRow === STRINGS.WITHDRAW
              ? layoutDataForWithdrawal && (
                  <FormRenderer
                    layout={layoutDataForWithdrawal?.layout_data as LayoutData}
                    handleFormSubmit={(values) => {
                      onUpdateClickWithdraw(values)
                    }}
                    heading={<SubmitButton />}
                    mode={'edit'}
                    data={dataForWithdrawal || null}
                    fullLayout={layoutDataForWithdrawal as EntityLayout}
                  />
                )
              : layoutEntityData && (
                  <>
                    <FormRenderer
                      layout={layoutData?.layout_data as LayoutData}
                      handleFormSubmit={(values) => {
                        handleFormSubmit(values)
                      }}
                      heading={
                        <>
                          {' '}
                          <SubmitButton />
                          {fillTypeDropdown?.length ? (
                            <CreateOrderFormComponent
                              setValue={setValue}
                              control={control}
                              fillTypeDropdownValues={fillTypeDropdown}
                              fulFillmentTypeDropdownValues={
                                fulFillmentTypeDropdown
                              }
                              distributorTypeDropdown={distributorTypeDropdown}
                            />
                          ) : null}
                        </>
                      }
                      mode={'edit'}
                      data={layoutEntityData || null}
                      fullLayout={layoutData as EntityLayout}
                    />
                    <ShippingDetailsFormComponent
                      register={register}
                      control={control}
                      shipToEntityDropDownValues={shipToEntityDropDownValues}
                      procedureForCreateOrder={procedureForApiCall}
                    />
                  </>
                )}
          </Modal>
        )}
        {enrollmentTableColumns?.length ? (
          <table className="w-full border-collapse table-fixed">
            <thead>
              {table?.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-t divide-x ">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`bg-secondary-50 text-left px-2 py-1 font-primary font-normal ${
                        header.id === 'expander'
                          ? 'text-right w-[3rem]'
                          : 'text-left'
                      }`}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!enrollmentData?.c_cases_parent_case?.length && (
                <tr>
                  <td
                    colSpan={enrollmentTableColumns?.length}
                    className="text-center py-4"
                  >
                    {STRINGS.NO_DATA_FOUND}
                  </td>
                </tr>
              )}
              {rowModel?.rows.map((row) => (
                <>
                  <tr className="border-t" key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-2 py-2 font-extralight">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr className="border-t">
                      <td
                        colSpan={enrollmentTableColumns?.length}
                        className="px-2 py-2 max-w-full overflow-x-auto"
                      >
                        <ShipmentSubTable
                          shipments={
                            row?.original?.c_case_shipment_details_case
                          }
                          shipmentColumns={shipmentTableColumns}
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        ) : null}
      </form>
    </Accordion>
  )
}

const ShippingDetailsFormComponent = ({
  register,
  control,
  shipToEntityDropDownValues,
  procedureForCreateOrder,
}: ShippingDetailsForm) => {
  return (
    <Accordion alwaysOpen heading={STRINGS.SHIPPING_DETAILS}>
      <form className="mt-4 grid grid-cols-5 gap-4 pb-4 px-4">
        <ControlledDropDown
          onChange={(e) => procedureForCreateOrder(e)}
          label="Ship To Entity"
          options={shipToEntityDropDownValues}
          name="ship_to_entity"
          control={control}
          required
        />
        <FormInput
          name="address_line_1"
          label="Address Line 1"
          register={register}
          disabled={true}
        />
        <FormInput
          name="address_line_2"
          label="Address Line 2"
          register={register}
          disabled={true}
        />
        <FormInput
          name="city"
          label="City"
          register={register}
          disabled={true}
        />
        <FormInput
          name="state"
          label="State"
          register={register}
          disabled={true}
        />
        <FormInput
          name="zipcode"
          label="Zip Code"
          register={register}
          disabled={true}
        />
      </form>
    </Accordion>
  )
}

const CreateOrderFormComponent = ({
  setValue,
  control,
  fillTypeDropdownValues,
  fulFillmentTypeDropdownValues,
  distributorTypeDropdown,
}: CreateOrderForm) => {
  const defaultValueMethod = (label: string) => {
    const listData =
      label === STRINGS.FILL_TYPE
        ? fillTypeDropdownValues
        : fulFillmentTypeDropdownValues
    const data = listData?.find((item: FieldValues) => item?.vorder === 1)
    return data?.value?.toString()
  }
  return (
    <Accordion alwaysOpen heading={STRINGS.CREATE_ORDER}>
      <form className="mt-4 grid grid-cols-3 gap-4 pb-4 px-4">
        <ControlledDropDown
          onChange={(e) => {
            setValue('fill_type', {
              id: e?.id,
              _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
            })
          }}
          label={STRINGS.FILL_TYPE}
          options={fillTypeDropdownValues ?? []}
          name="extraField_fill_type"
          defaultValue={defaultValueMethod(STRINGS.FILL_TYPE)}
          control={control}
        />
        <ControlledDropDown
          onChange={(e) => {
            setValue('fulfillment_type', {
              id: e?.id,
              _state: MULTI_UPLOAD_UTILS?.ADDED_STATE_UPLOAD,
            })
          }}
          label={STRINGS.FULFILLMENT_TYPE}
          options={fulFillmentTypeDropdownValues ?? []}
          name="extraField_fulfillment_type"
          defaultValue={defaultValueMethod(STRINGS.FULFILLMENT_TYPE)}
          control={control}
        />
        <ControlledDropDown
          onChange={(e) => {
            setValue('distributor', e?.value)
          }}
          label="Distributor"
          defaultValue={distributorTypeDropdown?.default_value}
          options={
            distributorTypeDropdown?.control_values?.map((val: FieldValues) => {
              return { label: val, value: val }
            }) ?? []
          }
          name="extraField_distributor"
          disabled={true}
          control={control}
        />
      </form>
    </Accordion>
  )
}
